"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NodesDebouncer = void 0;
/**
 * The timeout for which the node is considered to be loading.
 * If the node is not loaded after this timeout, it is considered to be
 * loaded or failed to be loaded, and allowed other places to proceed.
 *
 * Decrypting many nodes in parallel can take a lot of time, so we allow
 * more time for this.
 */
const DEBOUNCE_TIMEOUT = 5000;
/**
 * The timeout for which the node is considered to be waiting for a long time.
 * After this timeout the metric is sent.
 */
const DEBOUNCE_LONG_WAIT_TIMEOUT = 1000;
/**
 * Helper to avoid loading the same node twice.
 *
 * Each place that loads a node should report it is being loaded,
 * and when it is finished, it should report it is finished.
 * The finish must be called even if the node fails to be loaded
 * to clear the promise.
 *
 * Each place that loads a node from cache should first wait for
 * the node to be loaded if that is the case.
 */
class NodesDebouncer {
    telemetry;
    logger;
    promises = new Map();
    constructor(telemetry) {
        this.telemetry = telemetry;
        this.logger = telemetry.getLogger('nodes-debouncer');
        this.telemetry = telemetry;
    }
    loadingNodes(nodeUids) {
        for (const nodeUid of nodeUids) {
            this.loadingNode(nodeUid);
        }
    }
    loadingNode(nodeUid) {
        const { promise, resolve } = Promise.withResolvers();
        if (this.promises.has(nodeUid)) {
            this.logger.warn(`Loading twice for: ${nodeUid}`);
            return;
        }
        const timeout = setTimeout(() => {
            this.logger.warn(`Timeout for: ${nodeUid}`);
            this.finishedLoadingNode(nodeUid);
        }, DEBOUNCE_TIMEOUT);
        this.promises.set(nodeUid, { promise, resolve, timeout });
    }
    finishedLoadingNodes(nodeUids) {
        for (const nodeUid of nodeUids) {
            this.finishedLoadingNode(nodeUid);
        }
    }
    finishedLoadingNode(nodeUid) {
        const result = this.promises.get(nodeUid);
        if (!result) {
            return;
        }
        clearTimeout(result.timeout);
        result.resolve();
        this.promises.delete(nodeUid);
    }
    async waitForLoadingNode(nodeUid) {
        const result = this.promises.get(nodeUid);
        if (!result) {
            return;
        }
        const metricTimeout = setTimeout(() => {
            this.telemetry.recordMetric({
                eventName: 'debounceLongWait',
            });
        }, DEBOUNCE_LONG_WAIT_TIMEOUT);
        this.logger.debug(`Wait for: ${nodeUid}`);
        await result.promise;
        clearTimeout(metricTimeout);
    }
    clear() {
        for (const result of this.promises.values()) {
            clearTimeout(result.timeout);
            result.resolve();
        }
        this.promises.clear();
    }
}
exports.NodesDebouncer = NodesDebouncer;
//# sourceMappingURL=debouncer.js.map